import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConsulenzaDialog } from "@/components/accordi/ConsulenzaDialog";
import { useNoIndex, logoSrc, codiceKey } from "@/components/accordi/accordiUtils";
import { toast } from "sonner";
import { Building2, CheckCircle2, KeyRound, Landmark, Loader2, MessageCircle, ShieldCheck, Users } from "lucide-react";

// Landing pubblica dell'accordo: nessun prezzo, percentuale o sconto.
export default function AccordoLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(undefined);
  const [codice, setCodice] = useState("");
  const [busy, setBusy] = useState(false);
  const [consOpen, setConsOpen] = useState(false);
  const [codiceCons, setCodiceCons] = useState("");
  useNoIndex();

  useEffect(() => {
    const k = `accordo_lv_${slug}`;
    const traccia = sessionStorage.getItem(k) ? 0 : 1;
    sessionStorage.setItem(k, "1");
    api.get(`/public/accordi/landing/${slug}?traccia=${traccia}`).then(({ data }) => setA(data)).catch(() => setA(null));
  }, [slug]);

  const accedi = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/public/accordi/codice/${encodeURIComponent(codice.trim())}`);
      if (data.slug !== slug) throw new Error("Questo codice appartiene a un altro accordo");
      sessionStorage.setItem(codiceKey(slug), codice.trim().toUpperCase());
      navigate(`/accordi/${slug}/area`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const apriConsulenza = () => {
    setCodiceCons((sessionStorage.getItem(codiceKey(slug)) || codice || "").toUpperCase()); setConsOpen(true);
  };

  if (a === undefined) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const enti = a ? a.prodotti.filter((p) => p.ente) : [];
  const persone = a ? a.prodotti.filter((p) => !p.ente) : [];
  if (a === null) return <div className="flex min-h-screen items-center justify-center p-6 text-center text-muted-foreground" data-testid="accordo-non-disponibile">Accordo non disponibile o non più attivo.</div>;

  return (
    <div className="min-h-screen bg-bone" data-testid="accordo-landing">
      <header className="border-b border-border/60 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-5">
          <img src="/assets/assietica-logo.png" alt="Assietica" className="h-9" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Accordo di Maggior Favore</span>
        </div>
      </header>
      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-14 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl border bg-white p-2">
            {a.logo_url ? <img src={logoSrc(a.logo_url)} alt={a.denominazione} className="max-h-full max-w-full object-contain" data-testid="accordo-logo" /> : <Building2 className="h-9 w-9 text-forest/40" />}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{a.tipo_soggetto}</p>
            <h1 className="font-heading text-4xl font-semibold leading-tight text-forest sm:text-5xl" data-testid="accordo-nome">{a.denominazione}</h1>
          </div>
          {a.descrizione && <p className="max-w-2xl whitespace-pre-line text-base text-muted-foreground" data-testid="accordo-descrizione">{a.descrizione}</p>}
          {a.vantaggi.length > 0 && (
            <ul className="space-y-2" data-testid="accordo-vantaggi">
              {a.vantaggi.map((v) => <li key={v} className="flex gap-2 text-forest"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />{v}</li>)}
            </ul>
          )}
          {persone.length > 0 && a.beneficiari.length > 0 && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4" />Riservato a: {a.beneficiari.filter((b) => !b.startsWith("Ente aderente")).join(", ")} e loro familiari.</p>}
          {persone.length > 0 && (
            <div>
              <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Soluzioni per le persone</h2>
              <div className="grid gap-2 sm:grid-cols-2" data-testid="accordo-prodotti">
                {persone.map((p) => <div key={p.code} data-testid={`landing-prod-${p.code}`} className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2.5 text-sm text-forest"><ShieldCheck className="h-4 w-4 text-terracotta" />{p.label}</div>)}
              </div>
            </div>
          )}
          {enti.length > 0 && (
            <div className="space-y-3 rounded-2xl border border-forest/15 bg-sage/40 p-5" data-testid="accordo-prodotti-enti">
              <div>
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-forest"><Landmark className="h-5 w-5 text-terracotta" />Soluzioni per gli enti</h2>
                <p className="mt-1 text-sm text-muted-foreground">Per ETS, APS, ODV, Fondazioni e Associazioni, iscritti o non iscritti al RUNTS. Con il codice accordo calcoli subito il premio dove possibile; negli altri casi l'Agenzia prepara il preventivo dedicato.</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {enti.map((p) => <div key={p.code} data-testid={`landing-prod-${p.code}`} className="flex items-center gap-2 rounded-xl border bg-white px-3 py-2.5 text-sm text-forest"><ShieldCheck className="h-4 w-4 text-forest" />{p.label.replace(" (per l'ente)", "")}</div>)}
              </div>
            </div>
          )}
        </div>
        <aside className="order-first space-y-4 lg:order-none lg:col-span-2">
          <div className="space-y-4 rounded-2xl border bg-white p-6 shadow-sm lg:sticky lg:top-6">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-forest"><KeyRound className="h-5 w-5 text-terracotta" />Area riservata</h2>
            <p className="text-sm text-muted-foreground">Inserisci il codice accordo che ti ha comunicato il tuo ente: vedrai subito il tuo premio con le condizioni riservate.</p>
            <Input value={codice} onChange={(e) => setCodice(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && codice && accedi()} placeholder="Codice accordo" data-testid="landing-codice" className="h-11 text-center font-mono tracking-widest" />
            <Button onClick={accedi} disabled={!codice || busy} data-testid="landing-accedi" className="h-11 w-full bg-terracotta hover:bg-terracotta-hover">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Accedi e calcola il preventivo</Button>
            <button onClick={apriConsulenza} data-testid="landing-consulenza" className="flex w-full items-center justify-center gap-2 text-sm font-medium text-forest hover:underline"><MessageCircle className="h-4 w-4" />Richiedi una consulenza dedicata</button>
          </div>
          <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">Il diritto alle condizioni dell'accordo viene verificato solo quando decidi di procedere con la proposta. Assietica Soc. Coop. — intermediario iscritto al RUI.</p>
        </aside>
      </section>
      <ConsulenzaDialog open={consOpen} onOpenChange={setConsOpen} codice={codiceCons} slug={slug} />
    </div>
  );
}
